import { Stack, Text, Divider, Link } from '@ltvco/refresh-lib/theme';

import { SectionStack } from '../components';

import type { JudgeResult } from '../../../types';

const FJC_BASE_URL = 'https://www.fjc.gov/history/judges';

const normalizeSlugPart = (value?: string): string | undefined =>
  value
    ? value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-') || undefined
    : undefined;

/**
 * Build FJC judge profile URL slug from name (last-first-middle, lowercase, hyphens).
 * See https://www.fjc.gov/history/judges/kollar-kotelly-colleen
 */
function getFjcJudgeSlug(judge: {
  name_first?: string;
  name_last?: string;
  name_middle?: string;
}): string | null {
  const last = normalizeSlugPart(judge.name_last);
  const first = normalizeSlugPart(judge.name_first);
  if (!last || !first) return null;
  const middle = normalizeSlugPart(judge.name_middle);
  const parts = [last, first];
  if (middle) parts.push(middle);
  return parts.join('-');
}

const InfoField = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <Text>
    <strong>{label}:</strong> {value}
  </Text>
);

interface JudgeBiographicalInfoProps {
  judgeData: JudgeResult;
  courts?: string[];
  aba_rating?: string[];
}

export const JudgeBiographicalInfo = ({
  judgeData,
  courts,
  aba_rating,
}: JudgeBiographicalInfoProps) => {
  const abaFromArray = aba_rating?.filter(Boolean).join(', ')?.trim();
  const abaFromObj = judgeData.aba_ratings?.[0];
  const hasAbaRating = !!abaFromArray || !!abaFromObj?.rating;
  const abaDisplayValue = hasAbaRating
    ? `${abaFromArray || abaFromObj?.rating || ''}${
        abaFromObj?.year_rated ? ` (${abaFromObj.year_rated})` : ''
      }`.trim()
    : '';
  const hasBiographicalContent = abaDisplayValue.trim().length > 0;

  const fjcSlug = getFjcJudgeSlug(judgeData);
  const fjcUrl = fjcSlug ? `${FJC_BASE_URL}/${fjcSlug}` : null;

  return (
    <SectionStack direction="row">
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Text variant="h4" fontWeight="bold">
          {judgeData?.name_first} {judgeData?.name_middle}{' '}
          {judgeData?.name_last}
          {courts && courts.length > 0 && ` - ${courts.join(', ')}`}
        </Text>

        {fjcUrl ? (
          <Text variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            FJC ID:{' '}
            <Link href={fjcUrl} target="_blank" rel="noopener noreferrer">
              {fjcSlug} (Click here to know more!)
            </Link>
          </Text>
        ) : null}

        {hasBiographicalContent ? (
          <>
            <Divider />
            <Stack spacing={1} sx={{ mt: 1 }}>
              <InfoField label="ABA Rating" value={abaDisplayValue} />
            </Stack>
          </>
        ) : null}
      </Stack>
    </SectionStack>
  );
};
